import { z } from 'zod';
import { filterSchema, sortSchema, uuidSchema } from './common';

/**
 * Сохранённые представления грида — замена ручным фильтрам и «закрепить
 * строку» в Google Sheets. Личные и общие.
 */

export const viewConfigSchema = z.object({
  /** Видимые колонки в порядке отображения */
  columns: z.array(z.string()).max(60),
  /** Ширины, переопределённые пользователем */
  widths: z.record(z.string(), z.number().int().min(40).max(900)).default({}),
  /** Колонки, закреплённые слева */
  pinned: z.array(z.string()).max(8).default([]),
  /*
    Закрепление справа, фильтр по группе выезда и корзина раньше в срез
    не входили. Из-за этого срез «врал»: человек сохранял вид с закреплённым
    справа статусом и включённой корзиной, переключался на другой срез —
    и оба состояния молча оставались от предыдущего.
  */
  pinnedRight: z.array(z.string()).max(8).default([]),
  travelGroupId: uuidSchema.nullable().default(null),
  includeDeleted: z.boolean().default(false),
  sort: z.array(sortSchema).max(3).default([]),
  filters: z.array(filterSchema).max(20).default([]),
  search: z.string().max(100).optional(),
  /**
   * Поле группировки: ключ колонки или null. Заменяет разделители
   * «6 августа» из Google-таблицы.
   *
   * Группировка задаёт первичную сортировку — без этого строки одной
   * группы разъезжаются по выборке и группы вырождаются в строки по одной.
   */
  groupBy: z.string().max(64).nullable().default(null),
  /**
   * Прежний флаг «по дням». Оставлен, чтобы сохранённые до перехода
   * представления не ломались: читается как groupBy = 'addedDate'.
   * @deprecated используйте groupBy
   */
  groupByAddedDate: z.boolean().default(false),
  /** Плотность строк: влияет на высоту и на то, сколько влезает на экран */
  /*
    lines2 и lines4 — высота под несколько строк в ячейке. Появились
    ради телефонов: у человека их бывает под десяток, и в одну строку
    видно только первый. Так же устроен Airtable — высота строки задаётся
    на весь вид (1/2/4/6 строк), а не подбирается под каждую строку:
    виртуализация считает смещения по числу, и «каждая строка своей
    высоты» превращает прокрутку в угадайку.
  */
  density: z.enum(['compact', 'normal', 'comfortable', 'lines2', 'lines4']).default('normal'),
});
export type ViewConfig = z.infer<typeof viewConfigSchema>;
/** Высота строки таблицы — она же число строк текста в ячейке. */
export type GridDensity = ViewConfig['density'];

/** Поле группировки с учётом представлений, сохранённых до перехода на groupBy. */
export function resolveGroupBy(config: Pick<ViewConfig, 'groupBy' | 'groupByAddedDate'>): string | null {
  if (config.groupBy) return config.groupBy;
  return config.groupByAddedDate ? 'addedDate' : null;
}

export const savedViewSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  isShared: z.boolean(),
  isDefault: z.boolean(),
  ownerId: uuidSchema.nullable(),
  ownerName: z.string().nullable(),
  config: viewConfigSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SavedView = z.infer<typeof savedViewSchema>;

export const savedViewCreateSchema = z.object({
  name: z.string().trim().min(1, 'Укажите название').max(80),
  isShared: z.boolean().default(false),
  isDefault: z.boolean().default(false),
  config: viewConfigSchema,
});

export const savedViewUpdateSchema = savedViewCreateSchema.partial();
